import React, {useCallback, useEffect, useRef, useState} from "react";
import {Prompt, useHistory, useParams} from "react-router-dom";
import Button from "@source/components/UI/buttons/button/button";
import ConfirmModal from "@source/components/confirmModal/confirmModal";
import Notification from "@source/components/snackMessage/notification";
import ConsentController from "@source/api/controllers/consentController";
import ReqError from "@source/api/reqError";
import {ConsentTemplate, ConsentTemplateStatus} from "@source/api/models/consentModels";
import {
  buildTemplatePayload,
  hydrateTemplate,
  TemplateBuilderEditor,
  type TemplateBuilderState,
  type TemplatePayload,
} from "@source/components/consentDocuments/templateBuilder";
import {HOME_ROUTES} from "@source/utils/utils";
import {consentTemplateBuilderPath} from "@source/api/models/consentHelpers";
import {ConsentRequestState} from "@source/components/consentDocuments";
import styles from "./templateLibrary.module.scss";

const errorMessage = (error: unknown, fallback: string) => error instanceof Error ? error.message : fallback;
const payloadSignature = (title: string, payload: TemplatePayload) => JSON.stringify({title: title.trim(), payload});
const emptyPayload = () => {
  const state = hydrateTemplate();
  return buildTemplatePayload(state.data, state.inputDefinitions);
};

export const TemplateBuilderPage = ({
  organizationId,
  canManage,
  blank = false,
}: {
  organizationId: string;
  canManage: boolean;
  blank?: boolean;
}) => {
  const history = useHistory();
  const {templateId} = useParams<{templateId: string}>();
  const [template, setTemplate] = useState<ConsentTemplate | null>(null);
  const [title, setTitle] = useState("");
  const [payload, setPayload] = useState<TemplatePayload>(emptyPayload);
  const [validationIssues, setValidationIssues] = useState<string[]>([]);
  const [loading, setLoading] = useState(!blank);
  const [loadError, setLoadError] = useState("");
  const [unavailable, setUnavailable] = useState(false);
  const [mutationError, setMutationError] = useState("");
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const baseline = useRef(payloadSignature("", payload));
  const requestVersion = useRef(0);
  const routeVersion = useRef(0);
  const payloadRef = useRef(payload);
  const mounted = useRef(true);
  const mutationInProgress = useRef(false);
  const leaving = useRef(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    routeVersion.current += 1;
    requestVersion.current += 1;
    const nextPayload = emptyPayload();
    baseline.current = payloadSignature("", nextPayload);
    payloadRef.current = nextPayload;
    setTemplate(null);
    setTitle("");
    setPayload(nextPayload);
    setValidationIssues([]);
    setLoading(!blank);
    setLoadError("");
    setUnavailable(false);
    setDirty(false);
    setSaving(false);
    setCopying(false);
    setArchiving(false);
    mutationInProgress.current = false;
    leaving.current = false;
    setMutationError("");
  }, [blank, organizationId, templateId]);

  const updateDirty = useCallback((nextTitle = title, nextPayload = payloadRef.current) => {
    setDirty(payloadSignature(nextTitle, nextPayload) !== baseline.current);
  }, [title]);

  const load = useCallback(async () => {
    if (blank) return;
    const version = ++requestVersion.current;
    setLoading(true);
    setLoadError("");
    setUnavailable(false);
    try {
      const loaded = await ConsentController.getTemplate(organizationId, templateId);
      if (version !== requestVersion.current) return;
      const hydrated = hydrateTemplate(loaded);
      const nextPayload = buildTemplatePayload(hydrated.data, hydrated.inputDefinitions);
      baseline.current = payloadSignature(loaded.title, nextPayload);
      payloadRef.current = nextPayload;
      setTemplate(loaded);
      setTitle(loaded.title);
      setPayload(nextPayload);
      setDirty(false);
    } catch (error: unknown) {
      if (version === requestVersion.current) {
        if (error instanceof ReqError && error.status === 404) setUnavailable(true);
        else setLoadError(errorMessage(error, "Unable to load the template."));
      }
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [blank, organizationId, templateId]);

  useEffect(() => {
    if (blank) {
      const initial = hydrateTemplate();
      const nextPayload = buildTemplatePayload(initial.data, initial.inputDefinitions);
      baseline.current = payloadSignature("", nextPayload);
      payloadRef.current = nextPayload;
      setPayload(nextPayload);
      setDirty(false);
      return;
    }
    load();
    return () => { requestVersion.current += 1; };
  }, [blank, load]);

  const onBuilderChange = useCallback((_state: TemplateBuilderState, nextPayload: TemplatePayload, issues: string[]) => {
    payloadRef.current = nextPayload;
    setPayload(nextPayload);
    setValidationIssues(issues);
    updateDirty(undefined, nextPayload);
  }, [updateDirty]);

  const onTitleChange = (nextTitle: string) => {
    setTitle(nextTitle);
    updateDirty(nextTitle);
  };

  // A blank template deliberately has no persisted template or status until Save.
  const active = Boolean(template && template._id === templateId && template.status === ConsentTemplateStatus.ACTIVE);
  const editable = canManage && (blank || active);
  const titleError = !title.trim() ? "Enter a template title." : "";
  const contentError = !payload.structure.html.trim() ? "Add content to the template before saving." : "";
  const issues = [...validationIssues, ...(contentError ? [contentError] : [])];
  const busy = saving || copying || archiving;

  const warnBeforeLeaving = dirty && editable;
  useEffect(() => {
    if (!warnBeforeLeaving) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [warnBeforeLeaving]);
  // Saving, archiving and the discard dialog leave on purpose, so they skip the unsaved-changes prompt.
  const leave = useCallback((path: string) => {
    leaving.current = true;
    history.push(path);
  }, [history]);

  const cancel = useCallback(() => leave(HOME_ROUTES.ConsentDocumentsTemplates), [leave]);
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (mutationInProgress.current) return;
    if (!editable || titleError || issues.length) {
      setMutationError(titleError || issues[0] || "This template cannot be saved.");
      return;
    }
    mutationInProgress.current = true;
    setSaving(true);
    setMutationError("");
    const version = routeVersion.current;
    const body = {title: title.trim(), ...payloadRef.current};
    try {
      if (blank) {
        await ConsentController.createTemplate(organizationId, body);
        if (!mounted.current || version !== routeVersion.current) return;
        new Notification().success("Template created successfully.");
        leave(HOME_ROUTES.ConsentDocumentsTemplates);
      } else if (template) {
        const updated = await ConsentController.updateTemplate(organizationId, template._id, body);
        if (!mounted.current || version !== routeVersion.current) return;
        new Notification().success("Template updated successfully.");
        // Stay in the builder. The editor keeps its own state; only the
        // unsaved-changes baseline moves to what was just saved.
        const {title: savedTitle, ...savedPayload} = body;
        baseline.current = payloadSignature(savedTitle, savedPayload);
        if (updated?._id === template._id) setTemplate(updated);
        updateDirty(title, payloadRef.current);
      }
    } catch (error: unknown) {
      if (mounted.current && version === routeVersion.current) setMutationError(errorMessage(error, blank ? "Unable to create the template." : "Unable to update the template."));
    } finally {
      if (version === routeVersion.current) {
        mutationInProgress.current = false;
        if (mounted.current) setSaving(false);
      }
    }
  };

  const copy = useCallback(async () => {
    if (!template || !editable || mutationInProgress.current) return;
    mutationInProgress.current = true;
    setCopying(true);
    setMutationError("");
    const version = routeVersion.current;
    try {
      const currentTitle = title.trim() || template.title;
      const copied = await ConsentController.copyTemplate(organizationId, template._id, {title: `${currentTitle} (Copy)`});
      if (!mounted.current || version !== routeVersion.current) return;
      new Notification().success("Template copied successfully.");
      history.push(consentTemplateBuilderPath(copied._id));
    } catch (error: unknown) {
      if (mounted.current && version === routeVersion.current) setMutationError(errorMessage(error, "Unable to copy the template."));
    } finally {
      if (version === routeVersion.current) {
        mutationInProgress.current = false;
        if (mounted.current) setCopying(false);
      }
    }
  }, [editable, history, organizationId, template, title]);

  const archive = useCallback(async () => {
    if (!template || !editable || mutationInProgress.current) return;
    mutationInProgress.current = true;
    setArchiving(true);
    setMutationError("");
    const version = routeVersion.current;
    try {
      await ConsentController.archiveTemplate(organizationId, template._id);
      if (!mounted.current || version !== routeVersion.current) return;
      new Notification().success("Template archived.");
      leave(HOME_ROUTES.ConsentDocumentsTemplates);
    } catch (error: unknown) {
      if (mounted.current && version === routeVersion.current) setMutationError(errorMessage(error, "Unable to archive the template."));
    } finally {
      if (version === routeVersion.current) {
        mutationInProgress.current = false;
        if (mounted.current) setArchiving(false);
      }
    }
  }, [editable, leave, organizationId, template]);

  if (loading || (!blank && template?._id !== templateId)) return <ConsentRequestState variant="loading" title="Loading template"/>;
  if (loadError) return <ConsentRequestState variant="error" title="Unable to load template" description={loadError} onRetry={load}/>;
  if (unavailable || (!blank && !template)) return <ConsentRequestState variant="empty" title="Template unavailable" description="This template is no longer available." onRetry={load}/>;

  return <form className={styles.builder} onSubmit={save}>
    <Prompt
      when={warnBeforeLeaving}
      message={() => leaving.current || "You have unsaved template changes. Leave without saving?"}
    />
    <div className={styles.builderStatus}>
      {!editable ? <ConsentRequestState variant="empty" title="This template is read-only" description="Archived templates cannot be changed, copied, or archived again."/> : null}
      {mutationError ? <p className={styles.formError} role="alert">{mutationError}</p> : null}
    </div>
    <div
      className={`${styles.builderEditor} ${!editable ? styles.builderReadOnly : ""}`}
      aria-disabled={!editable}
      onKeyDown={!editable ? (event) => event.preventDefault() : undefined}
    >
      <TemplateBuilderEditor
        key={blank ? "blank" : template?._id}
        template={template || undefined}
        onStateChange={onBuilderChange}
        onCopy={editable && !blank ? copy : undefined}
        onArchive={editable && !blank ? archive : undefined}
        copyDisabled={busy}
        copyLoading={copying}
        archiveDisabled={busy}
        archiveLoading={archiving}
        title={title}
        editable={editable}
        titleDisabled={busy}
        onTitleChange={onTitleChange}
      />
    </div>
    <div className={styles.builderActions}>
      {dirty ? <ConfirmModal
        title="Discard template changes?"
        subTitle="Your unsaved template changes will be lost."
        confirmCb={cancel}
        confirmBtnInner="Discard changes"
        rejectBtnInner="Keep editing"
        hideIcon
      >
        <Button event="button" variant="outlined" disabled={busy}>Cancel</Button>
      </ConfirmModal> : <Button event="button" variant="outlined" onClick={cancel} disabled={busy}>Cancel</Button>}
      {editable ? <Button event="submit" disabled={busy || !!titleError || issues.length > 0} isLoading={saving}>{blank ? "Create template" : "Save changes"}</Button> : null}
    </div>
  </form>;
};
